import { useEffect, useRef, useState, type RefObject } from 'react';

const SEEK_INTERVAL = 1000 / 30;
const FRAME_TOLERANCE = 1 / 30;

type MagScrollVideoOptions = {
  sectionRef: RefObject<HTMLElement | null>;
  stageRef: RefObject<HTMLDivElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
  mediaRef: RefObject<HTMLDivElement | null>;
  enabled: boolean;
};

/** The caller mounts video sources only when both animated and nearby are true. */
export function useMagScrollVideo({
  sectionRef,
  stageRef,
  videoRef,
  mediaRef,
  enabled,
}: MagScrollVideoOptions) {
  const [motion, setMotion] = useState(false);
  const [nearby, setNearby] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const progressRef = useRef(0);
  const viewportInsideRef = useRef(false);
  const animated = enabled && motion && !failed;

  // Preferences stay subscribed while another carousel slide is selected.
  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = (navigator as Navigator & {
      connection?: EventTarget & { saveData?: boolean };
    }).connection;
    const sync = () => setMotion(!preference.matches && !connection?.saveData);
    sync();
    preference.addEventListener('change', sync);
    connection?.addEventListener('change', sync);
    return () => {
      preference.removeEventListener('change', sync);
      connection?.removeEventListener('change', sync);
    };
  }, []);

  useEffect(() => {
    if (!animated) {
      setNearby(false);
      setReady(false);
      progressRef.current = 0;
      mediaRef.current?.style.setProperty('--mag-progress', '0');
      return;
    }
    const section = sectionRef.current;
    if (!section) return;
    if (!('IntersectionObserver' in window)) {
      setNearby(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setNearby(true);
        observer.disconnect();
      }
    }, { rootMargin: '800px 0px' });
    observer.observe(section);
    return () => observer.disconnect();
  }, [animated, sectionRef, mediaRef]);

  useEffect(() => {
    const section = sectionRef.current;
    const stage = stageRef.current;
    const video = videoRef.current;
    const media = mediaRef.current;
    if (!animated || !nearby || !section || !stage || !video) return;

    setReady(false);
    let disposed = false;
    let frame = 0;
    let lastSeekAt = -Infinity;
    let seekWatchdog = 0;
    let loadWatchdog = 0;
    let hasFrame = false;

    const fail = () => {
      if (disposed) return;
      cancelAnimationFrame(frame);
      frame = 0;
      clearTimeout(seekWatchdog);
      clearTimeout(loadWatchdog);
      video.pause();
      setFailed(true);
      setReady(false);
    };
    const schedule = () => {
      if (!disposed && !frame && !document.hidden) frame = requestAnimationFrame(scrub);
    };
    const scrub = (now: number) => {
      frame = 0;
      if (disposed || document.hidden) return;
      const distance = Math.max(1, section.offsetHeight - stage.offsetHeight);
      const rect = section.getBoundingClientRect();
      const inset = parseFloat(getComputedStyle(stage).top) || 0;
      viewportInsideRef.current = rect.top <= inset + 1 && rect.bottom > 0;
      const progress = Math.max(0, Math.min(1, (inset - rect.top) / distance));
      progressRef.current = progress;
      media?.style.setProperty('--mag-progress', String(progress));

      if (video.readyState < 2 || !Number.isFinite(video.duration) || video.duration <= 0) return;
      // The decoder releases the next seek via seeked; scroll events coalesce.
      if (video.seeking) return;
      const end = Math.max(0, video.duration - FRAME_TOLERANCE);
      const start = Math.min(1, end);
      const target = start + progress * (end - start);
      if (Math.abs(video.currentTime - target) < FRAME_TOLERANCE) return;
      if (now - lastSeekAt < SEEK_INTERVAL) { schedule(); return; }
      lastSeekAt = now;
      try {
        video.currentTime = target;
        clearTimeout(seekWatchdog);
        seekWatchdog = window.setTimeout(fail, 5000);
      } catch { fail(); }
    };
    const loaded = () => {
      if (disposed || video.readyState < 2 || video.seeking) return;
      hasFrame = true;
      clearTimeout(loadWatchdog);
      const firstReadableFrame = Math.min(1, Math.max(0, video.duration - FRAME_TOLERANCE));
      if (video.currentTime >= firstReadableFrame - FRAME_TOLERANCE) setReady(true);
      schedule();
    };
    const seeked = () => {
      clearTimeout(seekWatchdog);
      loaded();
    };
    const visibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(frame);
        frame = 0;
        clearTimeout(seekWatchdog);
        clearTimeout(loadWatchdog);
      } else {
        if (!hasFrame) loadWatchdog = window.setTimeout(fail, 15000);
        if (video.seeking) seekWatchdog = window.setTimeout(fail, 5000);
        schedule();
      }
    };

    video.addEventListener('loadeddata', loaded);
    video.addEventListener('canplay', loaded);
    video.addEventListener('seeked', seeked);
    video.addEventListener('error', fail);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('pageshow', schedule);
    document.addEventListener('visibilitychange', visibility);
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    resize?.observe(section);
    resize?.observe(stage);

    video.muted = true;
    video.playsInline = true;
    video.pause();
    video.load();
    if (!document.hidden) loadWatchdog = window.setTimeout(fail, 15000);
    schedule();

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      clearTimeout(seekWatchdog);
      clearTimeout(loadWatchdog);
      resize?.disconnect();
      video.removeEventListener('loadeddata', loaded);
      video.removeEventListener('canplay', loaded);
      video.removeEventListener('seeked', seeked);
      video.removeEventListener('error', fail);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('pageshow', schedule);
      document.removeEventListener('visibilitychange', visibility);
      video.pause();
    };
  }, [animated, nearby, sectionRef, stageRef, videoRef, mediaRef]);

  return { animated, nearby: animated && nearby, ready: animated && ready, failed, progressRef, viewportInsideRef };
}

